import { FormattedMessage, injectIntl } from 'react-intl';
import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import DeliveryForm from './deliveryForm';
import { useDeliveryDelete } from '../apiHooks';
import Loader from '../../../components/loader';
import utils from '../../../utils/utils';
import ErrorMessage from '../../../components/errorMessage';
import DATE_FORMAT from '../../../components/constants/dateFormat';
import DELIVERY_STATUS from '../../../components/constants/deliveryStatus';
import DeliverySmsHistory from './deliverySmsHistory';
import SmsForm from './smsForm';
import PackerDeliveryOrderBatchRow from '../../packer/packerDeliveryOrderBatchRow';
import { Link } from 'react-router-dom';

const DeliveryTable = ({
  deliveries,
  deliveriesWithContent, // for overriding order content info
  intl,
  selectedDeliveryDay,
  routePlan,
  routes,
  routeId,
  showAddButton,
  searchResults,
}) => {
  const [editDelivery, setEditDelivery] = useState(null);
  const [smsViewDelivery, setSmsViewDelivery] = useState(null);
  const [showDeliveryAdd, setShowDeliveryAdd] = useState(false);
  const [showSmsForm, setShowSmsForm] = useState(false);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  const {
    deleteDelivery,
    isLoading: deleteDeliveryLoading,
    isError: isDeleteDeliveryError,
    error: deliveryDeleteError,
  } = useDeliveryDelete();

  const statuses = Object.keys(DELIVERY_STATUS).map((key) => {
    return DELIVERY_STATUS[key];
  });

  return (
    <>
      <Loader show={deleteDeliveryLoading} />
      <ErrorMessage error={isDeleteDeliveryError ? utils.getErrorMessageFromResponse(deliveryDeleteError) : null} />

      {showAddButton ? (
        <div className="m-b-3">
          <button
            onClick={() => {
              setShowDeliveryAdd(true);
            }}
            className={`btn btn-small btn-default m-r-2`}
          >
            <FontAwesomeIcon icon={faPlusCircle} />{' '}
            <FormattedMessage id={'admin.route.planning.edit.actions.add.stop'} />
          </button>
        </div>
      ) : (
        ''
      )}

      {deliveries.length ? (
        <table className="table table-striped admin__table">
          <thead>
            <tr>
              <th>
                <FormattedMessage id={'admin.route.planning.edit.route.table.name'} />
              </th>
              <th>
                <FormattedMessage id={'admin.route.planning.edit.route.table.address'} />
              </th>
              <th>
                <FormattedMessage id={'admin.route.planning.edit.route.table.delivery.order'} />
              </th>
              <th className="text-right">
                <div>
                  <FormattedMessage id={'admin.route.planning.edit.route.table.actions'} />
                </div>
                <div className="form-group m-b-0">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedDeliveries.length === deliveries.length}
                        onChange={() => {
                          if (selectedDeliveries.length === deliveries.length) {
                            setSelectedDeliveries([]);
                          } else {
                            setSelectedDeliveries(deliveries);
                          }
                        }}
                      />{' '}
                      <FormattedMessage id={'admin.route.planning.edit.route.table.actions.select.all'} />
                    </label>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {deliveries.map((delivery, index) => {
              const matchingSearchResult = searchResults?.find((result) => result.id === delivery.id);
              const status = statuses.find((s) => s.name === delivery.status);

              const selectedIndex = selectedDeliveries.findIndex((d) => d.id === delivery.id);
              const deliveryForContentFields =
                deliveriesWithContent?.find((d) => d.id === delivery.id) || matchingSearchResult || delivery;

              return (
                <tr
                  key={index}
                  id={delivery.id}
                  className={`${delivery.isIgnored ? 'text-muted' : ''} ${matchingSearchResult ? 'table-success' : ''}`}
                >
                  <td>
                    <div>{delivery.name}</div>
                    <div>
                      <strong>
                        <FormattedMessage id={'admin.route.planning.edit.route.table.status'} />
                      </strong>
                      : {status ? `${status.icon} ${status.name}` : delivery.status}
                    </div>
                    <div className="text-muted text-small">
                      <FormattedMessage id={'admin.route.planning.edit.route.table.delivery.id'} />: #{delivery.id}
                    </div>
                  </td>
                  <td>
                    {delivery.allowedDeliveryFrom ? (
                      <div>
                        <strong>
                          <FormattedMessage id={'admin.route.planning.edit.route.table.address.allowed.time.range'} />
                        </strong>
                        :{' '}
                        {utils.getFormattedDateRangeFromStrings(
                          delivery.allowedDeliveryFrom,
                          delivery.allowedDeliveryTo,
                          false
                        )}
                      </div>
                    ) : (
                      ''
                    )}
                    <div>
                      <strong>
                        <FormattedMessage id={'admin.route.planning.edit.route.table.address.eta'} />
                      </strong>
                      :{' '}
                      {delivery.estimatedArrivalTime
                        ? utils.getDateFromString(delivery.estimatedArrivalTime).format(DATE_FORMAT.onlyTime)
                        : '-'}
                    </div>
                    <div>
                      <strong>
                        <FormattedMessage id={'admin.route.planning.edit.route.table.address.arrival'} />
                      </strong>
                      :{' '}
                      {delivery.arrivalTime
                        ? utils.getDateFromString(delivery.arrivalTime).format(DATE_FORMAT.onlyTime)
                        : '-'}
                    </div>
                    <div>
                      <strong>
                        <FormattedMessage id={'admin.route.planning.edit.route.table.address.address'} />
                      </strong>
                      : {delivery.address}
                    </div>
                    {delivery.unit ? (
                      <div>
                        <strong>
                          <FormattedMessage id={'admin.route.planning.edit.route.table.address.unit'} />
                        </strong>
                        : {delivery.unit}
                      </div>
                    ) : (
                      ''
                    )}
                    {delivery.instructions ? (
                      <>
                        <strong>
                          <FormattedMessage id={'admin.route.planning.edit.route.table.address.instructions'} />
                        </strong>
                        :
                        <div className="text-rich-text" dangerouslySetInnerHTML={{ __html: delivery.instructions }} />
                      </>
                    ) : (
                      ''
                    )}
                    <div>
                      <strong>
                        <FormattedMessage id={'admin.route.planning.edit.route.table.address.phone'} />
                      </strong>
                      : {delivery.phoneNumber}
                    </div>
                    {delivery.waitingTimeInMinutes ? (
                      <div className="alert alert-warning m-t-2">
                        ⏱ <FormattedMessage id={'admin.route.planning.edit.route.table.address.wait.time'} />:{' '}
                        {delivery.waitingTimeInMinutes} min
                      </div>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    <div>
                      {deliveryForContentFields.deliveryOrderBatch ? (
                        <>
                          <div>
                            <strong>
                              <FormattedMessage id={'admin.route.planning.edit.route.table.dob.id'} />
                            </strong>
                            :{' '}
                            <button
                              className="btn btn-normal-link p-a-0"
                              onClick={() => setSelectedDelivery(deliveryForContentFields)}
                            >
                              {`#${deliveryForContentFields.deliveryOrderBatch.id}`}
                            </button>
                          </div>
                          <div className="text-small text-muted">
                            <FormattedMessage id={'admin.route.planning.edit.route.table.order.batch.ids'} />:{' '}
                            {deliveryForContentFields.deliveryOrderBatch.orders
                              .map((o) => o.batchId)
                              .filter((value, index, self) => self.indexOf(value) === index)
                              .map((batchId) => (
                                <Link key={batchId} to={`/admin/orderBatches/${batchId}`}>{`#${batchId} `}</Link>
                              ))}
                          </div>
                          <div className="text-small text-muted">
                            {deliveryForContentFields.deliveryOrderBatch.businessProfile ? (
                              <>
                                <FormattedMessage id={'admin.route.planning.edit.route.table.business.profile.id'} />:{' '}
                                <Link
                                  to={`/admin/businessProfiles`}
                                >{`#${deliveryForContentFields.deliveryOrderBatch.businessProfile.id} `}</Link>
                              </>
                            ) : (
                              <>
                                <FormattedMessage id={'admin.route.planning.edit.route.table.user.id'} />:{' '}
                                <Link
                                  to={`/admin/users/${deliveryForContentFields.deliveryOrderBatch.user.id}`}
                                >{`#${deliveryForContentFields.deliveryOrderBatch.user.id} `}</Link>
                              </>
                            )}
                          </div>
                          <div>
                            <strong>
                              <FormattedMessage id={'admin.route.planning.edit.route.table.dob.bag.count'} />
                            </strong>
                            : {deliveryForContentFields.deliveryOrderBatch.bagCount}
                          </div>
                          <div>
                            <strong>
                              <FormattedMessage id={'admin.route.planning.edit.route.table.dob.thermo.bags'} />
                            </strong>
                            : {deliveryForContentFields.deliveryOrderBatch.thermoBoxIds}
                          </div>

                          {deliveryForContentFields.deliveryOrderBatch.customTypes ? (
                            <div>
                              {deliveryForContentFields.deliveryOrderBatch.customTypes.map((customType, index) => (
                                <div key={index}>
                                  {customType.icon} {customType.label}:{' '}
                                  <strong>{customType.showQuantity ? customType.quantity : '✅'}</strong>
                                </div>
                              ))}
                            </div>
                          ) : (
                            ''
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                    {delivery.content ? (
                      <div className="text-rich-text" dangerouslySetInnerHTML={{ __html: delivery.content }} />
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-right">
                    <div>
                      <button onClick={() => setEditDelivery(delivery)} className="btn btn-normal-link">
                        <FormattedMessage id={'admin.route.planning.edit.route.table.actions.edit'} />
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              intl.formatMessage({ id: 'admin.route.planning.edit.route.table.actions.delete.confirm' })
                            )
                          ) {
                            deleteDelivery({ deliveryId: delivery.id });
                          }
                        }}
                        className="btn btn-normal-link"
                      >
                        <FormattedMessage id={'admin.route.planning.edit.route.table.actions.delete'} />
                      </button>
                    </div>
                    <div>
                      <button onClick={() => setSmsViewDelivery(delivery)} className="btn btn-normal-link">
                        <FormattedMessage
                          id={'admin.route.planning.edit.route.table.actions.view.sms'}
                          values={{ count: delivery.smses?.length }}
                        />
                      </button>
                    </div>
                    <div className="form-group m-b-1">
                      <div className="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedIndex !== -1}
                            onChange={() => {
                              if (selectedIndex === -1) {
                                setSelectedDeliveries([...selectedDeliveries, delivery]);
                              } else {
                                const newSelectedDeliveries = [...selectedDeliveries];
                                newSelectedDeliveries.splice(selectedIndex, 1);
                                setSelectedDeliveries(newSelectedDeliveries);
                              }
                            }}
                          />{' '}
                          <FormattedMessage id={'admin.route.planning.edit.route.table.actions.select.single'} />
                        </label>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        ''
      )}

      {selectedDeliveries.length ? (
        <div className="text-right">
          <button className="btn btn-primary" onClick={() => setShowSmsForm(true)}>
            <FormattedMessage
              id={'admin.route.planning.edit.route.table.actions.send.sms'}
              values={{ count: selectedDeliveries.length }}
            />
          </button>
        </div>
      ) : (
        ''
      )}

      <Modal show={showSmsForm} onHide={() => setShowSmsForm(false)} size="lg">
        <div onClick={() => setShowSmsForm(false)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {showSmsForm ? (
            <SmsForm selectedDeliveries={selectedDeliveries} onSuccess={() => setShowSmsForm(false)} />
          ) : (
            ''
          )}
        </div>
      </Modal>
      <Modal show={showDeliveryAdd} onHide={() => setShowDeliveryAdd(false)} size="lg">
        <div onClick={() => setShowDeliveryAdd(false)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {showDeliveryAdd ? (
            <DeliveryForm
              routes={routes}
              deliveryDayId={selectedDeliveryDay.id}
              routePlanId={routePlan?.id}
              onSuccess={() => {
                setShowDeliveryAdd(false);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
      <Modal show={!!editDelivery} onHide={() => setEditDelivery(null)} size="lg">
        <div onClick={() => setEditDelivery(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {editDelivery ? (
            <DeliveryForm
              routeId={routeId}
              routes={routes}
              existingDelivery={editDelivery}
              deliveryDayId={selectedDeliveryDay.id}
              routePlanId={routePlan?.id}
              onSuccess={() => {
                setEditDelivery(null);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
      <Modal show={!!selectedDelivery} onHide={() => setSelectedDelivery(null)} size="lg">
        <div onClick={() => setSelectedDelivery(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {selectedDelivery ? <PackerDeliveryOrderBatchRow delivery={selectedDelivery} /> : ''}
        </div>
      </Modal>
      <Modal show={!!smsViewDelivery} onHide={() => setSmsViewDelivery(null)} size="lg">
        <div onClick={() => setSmsViewDelivery(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">{smsViewDelivery ? <DeliverySmsHistory delivery={smsViewDelivery} /> : ''}</div>
      </Modal>
    </>
  );
};

export default injectIntl(DeliveryTable);
